import React from 'react';


const ImpactPreview = ({ formData, business, campaigns, leaderboard, totalRaised, totalGoal, avgESG, compact, onTextPositionChange }) => {
  const scale = compact ? 'scale-[0.7] origin-top' : '';
  const textPosition = formData.hero_text_position || { x: 50, y: 50 };
  const textAlign = formData.hero_text_align || 'center';
  const sectionOrder = formData.section_order || ['stats', 'about', 'leaderboard', 'testimonials', 'featured', 'campaigns'];
  
  // Handle drag for text positioning
  const handleHeroDrag = (e) => {
    if (!onTextPositionChange) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = Math.max(10, Math.min(90, ((e.clientX - rect.left) / rect.width) * 100));
    const y = Math.max(15, Math.min(85, ((e.clientY - rect.top) / rect.height) * 100));
    onTextPositionChange({ x: Math.round(x), y: Math.round(y) });
  };

  // Render function for each section
  const renderSection = (sectionId) => {
    switch (sectionId) {
      case 'stats':
        return formData.show_stats ? (
          <div key="stats" className="py-12 px-8 bg-white">
            <div className="max-w-4xl mx-auto grid grid-cols-4 gap-6">
              {[
                { value: `$${(totalRaised/1000).toFixed(0)}K`, label: 'Total Raised', color: formData.primary_color },
                { value: campaigns.length, label: 'Active Campaigns', color: '#10B981' },
                { value: avgESG, label: 'ESG Score', color: '#8B5CF6' },
                { value: `${Math.round((totalRaised/Math.max(totalGoal,1))*100)}%`, label: 'Goal Progress', color: '#F59E0B' }
              ].map((stat, i) => (
                <div key={i} className="text-center p-4 rounded-xl" style={{ background: `${stat.color}10` }}>
                  <p className="text-3xl font-bold" style={{ color: stat.color }}>{stat.value}</p>
                  <p className="text-sm text-gray-600">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        ) : null;

      case 'about':
        return formData.show_about ? (
          <div key="about" className="py-12 px-8" style={{ background: '#FAF7F2' }}>
            <div className="max-w-4xl mx-auto">
              <div className={`grid ${formData.company_image_url ? 'grid-cols-2 gap-8 items-center' : ''}`}>
                {formData.company_image_url && (
                  <img src={formData.company_image_url} alt="Company" className="rounded-xl shadow-lg" />
                )}
                <div>
                  <h2 className="text-2xl font-bold mb-4" style={{ color: formData.primary_color }}>About Us</h2>
                  {formData.mission_statement && (
                    <p className="text-lg font-medium text-gray-700 mb-4 italic">&ldquo;{formData.mission_statement}&rdquo;</p>
                  )}
                  <p className="text-gray-600 leading-relaxed">{formData.about_text}</p>
                </div>
              </div>
            </div>
          </div>
        ) : null;

      case 'trends':
        return formData.show_trends && ((formData.charity_trends?.length > 0) || (formData.industry_stats?.length > 0) || (formData.news_items?.length > 0)) ? (
          <div key="trends" className="py-16 px-8 bg-white">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-2xl font-bold mb-2 text-center" style={{ color: formData.primary_color }}>
                {formData.trends_title || 'Charity Trends & Insights'}
              </h2>
              <p className="text-gray-500 text-center mb-10">Understanding the impact of corporate giving</p>
              
              {/* Industry Statistics */}
              {(formData.industry_stats?.length > 0) && (
                <div className="mb-12">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {formData.industry_stats.map((stat, i) => (
                      <div key={i} className="text-center p-6 rounded-xl" style={{ background: `${formData.primary_color}08` }}>
                        <p className="text-3xl font-bold mb-1" style={{ color: formData.primary_color }}>{stat.value}</p>
                        <p className="text-sm text-gray-600 mb-2">{stat.label}</p>
                        {stat.source && <p className="text-xs text-gray-400">{stat.source}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Charity Trends */}
              {(formData.charity_trends?.length > 0) && (
                <div className="mb-12">
                  <h3 className="text-lg font-semibold mb-6 text-gray-800">Key Trends in Corporate Giving</h3>
                  <div className="grid md:grid-cols-3 gap-6">
                    {formData.charity_trends.map((trend, i) => (
                      <div key={i} className="p-6 rounded-xl border" style={{ borderColor: '#E8DFD0' }}>
                        <div className="text-3xl mb-3">{trend.icon}</div>
                        <h4 className="font-semibold text-gray-900 mb-2">{trend.title}</h4>
                        <p className="text-sm text-gray-600">{trend.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* News Items */}
              {(formData.news_items?.length > 0) && (
                <div>
                  <h3 className="text-lg font-semibold mb-6 text-gray-800">Latest News & Updates</h3>
                  <div className="space-y-4">
                    {formData.news_items.map((news, i) => (
                      <div key={i} className="flex gap-4 p-4 rounded-lg hover:bg-gray-50 transition-all">
                        <div className="text-sm text-gray-400 whitespace-nowrap">
                          {new Date(news.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-1">{news.headline}</h4>
                          <p className="text-sm text-gray-600">{news.summary}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : null;

      case 'leaderboard':
        return formData.show_leaderboard ? (
          <div key="leaderboard" className="py-12 px-8 bg-white">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl font-bold mb-8 text-center" style={{ color: formData.primary_color }}>
                {formData.leaderboard_title}
              </h2>
              <div className="grid grid-cols-5 gap-4">
                {leaderboard.map((person, i) => (
                  <div key={i} className="text-center">
                    <div className={`relative mx-auto mb-3 ${i === 0 ? 'scale-110' : ''}`}>
                      <div 
                        className="w-16 h-16 rounded-full flex items-center justify-center text-white text-xl font-bold mx-auto"
                        style={{ background: i === 0 ? '#F59E0B' : i === 1 ? '#9CA3AF' : i === 2 ? '#CD7F32' : formData.primary_color }}
                      >
                        {person.name.split(' ').map(n => n[0]).join('')}
                      </div>
                      {i < 3 && (
                        <div className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-white shadow flex items-center justify-center">
                          <span className="text-sm">{i === 0 ? '🥇' : i === 1 ? '🥈' : '🥉'}</span>
                        </div>
                      )}
                    </div>
                    <p className="font-semibold text-sm text-gray-900">{person.name.split(' ')[0]}</p>
                    <p className="text-xs text-gray-500">${person.total.toLocaleString()}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : null;

      case 'testimonials':
        return formData.show_testimonials && formData.testimonials.length > 0 ? (
          <div key="testimonials" className="py-12 px-8" style={{ background: '#FAF7F2' }}>
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl font-bold mb-8 text-center" style={{ color: formData.primary_color }}>
                What People Say
              </h2>
              <div className="grid grid-cols-2 gap-6">
                {formData.testimonials.map((t, i) => (
                  <div key={i} className="bg-white rounded-xl p-6 shadow-md">
                    <div className="flex gap-1 mb-3">
                      {[1,2,3,4,5].map(star => (
                        <span key={star} className={`text-lg ${star <= (t.rating || 5) ? 'text-yellow-400' : 'text-gray-200'}`}>★</span>
                      ))}
                    </div>
                    <p className="text-gray-600 italic mb-4">&ldquo;{t.text}&rdquo;</p>
                    <div>
                      <p className="font-semibold text-gray-900">{t.name}</p>
                      <p className="text-sm text-gray-500">{t.role}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : null;

      case 'featured':
        return formData.show_featured_section && formData.featured_campaign_ids?.length > 0 ? (
          <div key="featured" className="py-16 px-8" style={{ background: `linear-gradient(135deg, ${formData.primary_color}08 0%, ${formData.secondary_color}15 100%)` }}>
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-10">
                <span className="inline-block px-4 py-1 rounded-full text-sm font-semibold mb-3" 
                  style={{ background: `${formData.primary_color}15`, color: formData.primary_color }}>
                  ⭐ FEATURED
                </span>
                <h2 className="text-3xl font-bold" style={{ color: formData.primary_color }}>
                  {formData.featured_section_title || 'Featured Causes'}
                </h2>
                <p className="text-gray-500 mt-2 max-w-lg mx-auto">
                  These are our top priority campaigns making the biggest impact right now.
                </p>
              </div>
              
              <div className="grid grid-cols-3 gap-6">
                {formData.featured_campaign_ids.slice(0, 3).map((campaignId, index) => {
                  const campaign = campaigns.find(c => c.campaign_id === campaignId);
                  if (!campaign) return null;
                  
                  const progress = Math.min((campaign.raised_amount || 0) / (campaign.goal_amount || 1) * 100, 100);
                  const impactValue = Math.round((campaign.raised_amount || 0) * (campaign.impact_conversion_rate || 1));
                  
                  return (
                    <div 
                      key={campaignId} 
                      className="bg-white rounded-2xl shadow-lg overflow-hidden border-2 transition-all hover:shadow-xl hover:-translate-y-1"
                      style={{ borderColor: index === 0 ? formData.primary_color : '#E8DFD0' }}
                    >
                      {index === 0 && (
                        <div className="px-4 py-2 text-center text-white text-sm font-semibold"
                          style={{ background: formData.primary_color }}>
                          🏆 TOP FEATURED
                        </div>
                      )}
                      
                      <div className="p-6">
                        <div className="mb-4">
                          <h3 className="font-bold text-lg text-gray-900 mb-1">{campaign.title}</h3>
                          <p className="text-sm font-medium" style={{ color: formData.secondary_color }}>
                            {campaign.charity_name}
                          </p>
                          {campaign.charity_ein && (
                            <p className="text-xs text-gray-400 mt-1">EIN: {campaign.charity_ein}</p>
                          )}
                        </div>
                        
                        {campaign.description && (
                          <p className="text-sm text-gray-600 mb-4 line-clamp-2">{campaign.description}</p>
                        )}
                        
                        <div className="mb-4">
                          <div className="flex justify-between text-sm mb-2">
                            <span className="font-semibold text-gray-900">${(campaign.raised_amount || 0).toLocaleString()}</span>
                            <span className="text-gray-500">of ${(campaign.goal_amount || 0).toLocaleString()}</span>
                          </div>
                          <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden">
                            <div className="h-full rounded-full transition-all" style={{ width: `${progress}%`, background: formData.primary_color }} />
                          </div>
                          <p className="text-xs text-gray-500 mt-1">{Math.round(progress)}% funded</p>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-2 mb-4 p-3 rounded-lg bg-gray-50">
                          <div className="text-center">
                            <p className="text-lg font-bold" style={{ color: formData.primary_color }}>{campaign.esg_score || '--'}</p>
                            <p className="text-xs text-gray-500">ESG Score</p>
                          </div>
                          <div className="text-center">
                            <p className="text-lg font-bold text-green-600">{impactValue > 0 ? impactValue.toLocaleString() : '--'}</p>
                            <p className="text-xs text-gray-500">{campaign.impact_metric || 'Impact'}</p>
                          </div>
                        </div>
                        
                        {campaign.employer_match_enabled && (
                          <div className="mb-4 p-2 rounded-lg text-center text-sm"
                            style={{ background: `${formData.secondary_color}20`, color: formData.secondary_color }}>
                            ✨ {campaign.employer_match_ratio || 1}x Employer Match Active
                          </div>
                        )}
                        
                        <button 
                          className="w-full py-3 rounded-xl font-bold text-white transition-all hover:opacity-90 hover:shadow-md"
                          style={{ background: formData.primary_color }}
                        >
                          💝 Donate Now
                        </button>
                        
                        {/* Social Share Section */}
                        <div className="mt-4 pt-4 border-t border-gray-100">
                          <p className="text-xs text-gray-500 text-center mb-2">Share this cause</p>
                          <div className="flex justify-center gap-2">
                            {(() => {
                              const shareUrl = `${window.location.origin}/impact/${business?.id}/campaign/${campaign.campaign_id}`;
                              const shareText = `🌟 I'm supporting "${campaign.title}" by ${campaign.charity_name}!\n\n💰 Goal: $${(campaign.goal_amount || 0).toLocaleString()}\n📊 ESG Score: ${campaign.esg_score || 'N/A'}\n\nJoin me in making a difference! 💝`;
                              return (
                                <>
                                  {/* Facebook */}
                                  <a 
                                    href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="w-9 h-9 rounded-full flex items-center justify-center transition-all hover:scale-110"
                                    style={{ background: '#1877F2' }}
                                    title="Share on Facebook"
                                  >
                                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                                    </svg>
                                  </a>
                                  {/* Twitter/X */}
                                  <a 
                                    href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="w-9 h-9 rounded-full flex items-center justify-center bg-black transition-all hover:scale-110"
                                    title="Share on X"
                                  >
                                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                                    </svg>
                                  </a>
                                  {/* LinkedIn */}
                                  <a 
                                    href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="w-9 h-9 rounded-full flex items-center justify-center transition-all hover:scale-110"
                                    style={{ background: '#0A66C2' }}
                                    title="Share on LinkedIn"
                                  >
                                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                                    </svg>
                                  </a>
                                  {/* WhatsApp */}
                                  <a 
                                    href={`https://wa.me/?text=${encodeURIComponent(`${shareText}\n\n${shareUrl}`)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="w-9 h-9 rounded-full flex items-center justify-center transition-all hover:scale-110"
                                    style={{ background: '#25D366' }}
                                    title="Share on WhatsApp"
                                  >
                                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                                    </svg>
                                  </a>
                                  {/* Email */}
                                  <a 
                                    href={`mailto:?subject=${encodeURIComponent(`Join me in supporting ${campaign.title}! 🌟`)}&body=${encodeURIComponent(`Hi there!\n\nI wanted to share an amazing cause with you:\n\n🎯 Campaign: ${campaign.title}\n🏢 Organization: ${campaign.charity_name}\n💰 Goal: $${(campaign.goal_amount || 0).toLocaleString()}\n📊 ESG Score: ${campaign.esg_score || 'N/A'}\n\n${campaign.description || 'Help make a difference today!'}\n\n✨ Every contribution makes a real impact!\n\n👉 Learn more and donate: ${shareUrl}\n\nTogether, we can create lasting change! 💝`)}`}
                                    className="w-9 h-9 rounded-full flex items-center justify-center transition-all hover:scale-110"
                                    style={{ background: formData.secondary_color }}
                                    title="Share via Email"
                                  >
                                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                                    </svg>
                                  </a>
                                  {/* Copy Link */}
                                  <button 
                                    onClick={() => {
                                      navigator.clipboard.writeText(shareUrl);
                                      alert('Link copied to clipboard!');
                                    }}
                                    className="w-9 h-9 rounded-full flex items-center justify-center bg-gray-500 transition-all hover:scale-110 hover:bg-gray-600"
                                    title="Copy Link"
                                  >
                                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3"/>
                                    </svg>
                                  </button>
                                </>
                              );
                            })()}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ) : null;

      case 'campaigns':
        return (
          <div key="campaigns" className="py-12 px-8 bg-white">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-2xl font-bold mb-8 text-center" style={{ color: formData.primary_color }}>
                {formData.featured_campaign_ids?.length > 0 ? 'More Campaigns' : 'Our Active Campaigns'}
              </h2>
              <div className="grid grid-cols-2 gap-6">
                {campaigns
                  .filter(c => !formData.featured_campaign_ids?.includes(c.campaign_id))
                  .slice(0, 4)
                  .map((campaign, i) => {
                    const progress = Math.min((campaign.raised_amount || 0) / (campaign.goal_amount || 1) * 100, 100);
                    const impactValue = Math.round((campaign.raised_amount || 0) * (campaign.impact_conversion_rate || 1));
                    
                    return (
                      <div key={i} className="border rounded-xl p-5 hover:shadow-lg transition-all bg-white" style={{ borderColor: '#E8DFD0' }}>
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <h3 className="font-bold text-gray-900">{campaign.title}</h3>
                            <p className="text-sm" style={{ color: formData.secondary_color }}>{campaign.charity_name}</p>
                          </div>
                          {campaign.esg_score && (
                            <span className="px-2 py-1 rounded-full text-xs font-semibold"
                              style={{ background: `${formData.primary_color}15`, color: formData.primary_color }}>
                              ESG {campaign.esg_score}
                            </span>
                          )}
                        </div>
                        
                        {campaign.description && (
                          <p className="text-sm text-gray-500 mb-3 line-clamp-2">{campaign.description}</p>
                        )}
                        
                        <div className="mb-3">
                          <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden mb-1">
                            <div className="h-full rounded-full" style={{ width: `${progress}%`, background: formData.primary_color }} />
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">${(campaign.raised_amount || 0).toLocaleString()} raised</span>
                            <span className="font-medium" style={{ color: formData.primary_color }}>{Math.round(progress)}%</span>
                          </div>
                        </div>
                        
                        <div className="flex items-center justify-between mb-3">
                          {impactValue > 0 && (
                            <span className="text-xs text-green-600">🌱 {impactValue.toLocaleString()} {campaign.impact_metric || 'impacted'}</span>
                          )}
                          {campaign.employer_match_enabled && (
                            <span className="text-xs" style={{ color: formData.secondary_color }}>✨ {campaign.employer_match_ratio || 1}x Match</span>
                          )}
                        </div>
                        
                        <button 
                          className="w-full py-2.5 rounded-lg font-semibold text-white transition-all hover:opacity-90"
                          style={{ background: formData.primary_color }}
                        >
                          Donate
                        </button>
                        
                        {/* Social Share Section */}
                        <div className="mt-3 pt-3 border-t border-gray-100">
                          {(() => {
                            const shareUrl = `${window.location.origin}/impact/${business?.id}/campaign/${campaign.campaign_id}`;
                            const shareText = `🌟 Support "${campaign.title}" by ${campaign.charity_name}! 💝`;
                            return (
                              <div className="flex items-center justify-between">
                                <span className="text-xs text-gray-400">Share:</span>
                                <div className="flex gap-1.5">
                                  {/* Facebook */}
                                  <a 
                                    href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="w-7 h-7 rounded-full flex items-center justify-center transition-all hover:scale-110"
                                    style={{ background: '#1877F2' }}
                                    title="Share on Facebook"
                                  >
                                    <svg className="w-3.5 h-3.5 text-white" fill="currentColor" viewBox="0 0 24 24">
                                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                                    </svg>
                                  </a>
                                  {/* Twitter/X */}
                                  <a 
                                    href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="w-7 h-7 rounded-full flex items-center justify-center bg-black transition-all hover:scale-110"
                                    title="Share on X"
                                  >
                                    <svg className="w-3.5 h-3.5 text-white" fill="currentColor" viewBox="0 0 24 24">
                                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                                    </svg>
                                  </a>
                                  {/* LinkedIn */}
                                  <a 
                                    href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="w-7 h-7 rounded-full flex items-center justify-center transition-all hover:scale-110"
                                    style={{ background: '#0A66C2' }}
                                    title="Share on LinkedIn"
                                  >
                                    <svg className="w-3.5 h-3.5 text-white" fill="currentColor" viewBox="0 0 24 24">
                                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                                    </svg>
                                  </a>
                                  {/* WhatsApp */}
                                  <a 
                                    href={`https://wa.me/?text=${encodeURIComponent(`${shareText}\n${shareUrl}`)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="w-7 h-7 rounded-full flex items-center justify-center transition-all hover:scale-110"
                                    style={{ background: '#25D366' }}
                                    title="Share on WhatsApp"
                                  >
                                    <svg className="w-3.5 h-3.5 text-white" fill="currentColor" viewBox="0 0 24 24">
                                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                                    </svg>
                                  </a>
                                  {/* Email */}
                                  <a 
                                    href={`mailto:?subject=${encodeURIComponent(`Support ${campaign.title} 🌟`)}&body=${encodeURIComponent(`Check out this amazing cause:\n\n${campaign.title} by ${campaign.charity_name}\n\n👉 ${shareUrl}`)}`}
                                    className="w-7 h-7 rounded-full flex items-center justify-center transition-all hover:scale-110"
                                    style={{ background: formData.secondary_color }}
                                    title="Share via Email"
                                  >
                                    <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                                    </svg>
                                  </a>
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    );
                  })}
              </div>
              
              {campaigns.length === 0 && (
                <div className="text-center py-12">
                  <p className="text-gray-400 text-lg">No active campaigns yet</p>
                  <p className="text-gray-300 text-sm mt-1">Campaigns will appear here once created</p>
                </div>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };
  
  return (
    <div className={compact ? scale : ''}>
      {/* Header with Logo - Always at top */}
      <div className="bg-white px-6 py-4 flex items-center justify-between border-b">
        <div className="flex items-center gap-3">
          {formData.logo_url ? (
            <img src={formData.logo_url} alt="Logo" className="h-20 object-contain" />
          ) : (
            <div className="font-bold text-lg" style={{ color: formData.primary_color }}>
              {business?.name || 'Your Company'}
            </div>
          )}
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-500">About</span>
          <span className="text-sm text-gray-500">Campaigns</span>
          <span className="text-sm text-gray-500">Impact</span>
          <button 
            className="px-4 py-2 rounded-full text-sm font-medium text-white"
            style={{ background: formData.primary_color }}
          >
            Donate
          </button>
        </div>
      </div>

      {/* Hero Section - Always after header */}
      <div 
        className="relative min-h-[400px] flex items-center justify-center cursor-move"
        style={
          formData.hero_style === 'image' && formData.hero_image_url
            ? { backgroundImage: `linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url(${formData.hero_image_url})`, backgroundSize: 'cover', backgroundPosition: 'center' }
            : formData.hero_style === 'minimal'
              ? { background: '#FAF7F2', minHeight: '350px' }
              : { background: `linear-gradient(135deg, ${formData.primary_color} 0%, ${formData.secondary_color} 100%)` }
        }
        onClick={handleHeroDrag}
      >
        <div 
          className="absolute px-8 max-w-2xl transition-all duration-200"
          style={{ 
            left: `${textPosition.x}%`, 
            top: `${textPosition.y}%`, 
            transform: 'translate(-50%, -50%)',
            textAlign: textAlign
          }}
        >
          <h1 className={`text-4xl font-bold mb-4 ${formData.hero_style === 'minimal' ? 'text-gray-900' : 'text-white'}`}>
            {formData.hero_title}
          </h1>
          <p className={`text-xl mb-8 ${formData.hero_style === 'minimal' ? 'text-gray-600' : 'text-white/90'}`}>
            {formData.hero_subtitle}
          </p>
          <button 
            className="px-8 py-3 rounded-full font-bold text-lg shadow-lg transition-all hover:scale-105"
            style={{ 
              background: formData.hero_style === 'minimal' ? formData.primary_color : 'white',
              color: formData.hero_style === 'minimal' ? 'white' : formData.primary_color
            }}
          >
            {formData.call_to_action_text}
          </button>
        </div>

        {onTextPositionChange && (
          <div className="absolute bottom-2 right-2 bg-black/50 text-white text-xs px-2 py-1 rounded">
            Click to position text
          </div>
        )}
      </div>

      {/* Render sections in custom order */}
      {sectionOrder.map(sectionId => renderSection(sectionId))}

      {/* Footer - Always at bottom */}
      <div className="py-8 px-8 text-center" style={{ background: formData.primary_color }}>
        <p className="text-white/80 text-sm">
          {formData.footer_text || `© ${new Date().getFullYear()} ${business?.name || 'Company'}. All rights reserved.`}
        </p>
        {Object.keys(formData.social_links).length > 0 && (
          <div className="flex justify-center gap-4 mt-4">
            {Object.entries(formData.social_links).filter(([_, url]) => url).map(([platform, url]) => (
              <a key={platform} href={url} className="text-white/80 hover:text-white capitalize text-sm">
                {platform}
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ImpactPreview;
